import styled from 'styled-components';
import { darken } from 'polished';

export const Container = styled.div`
  font-family: 'Sora', sans-serif;
  padding: 50px;
  display: flex;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.white};

  * {
    letter-spacing: 0px;
    line-height: initial;
  }

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoints.xxl}px) {
    padding: 30px;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    padding: 20px;
  }
`;

export const Wrapper = styled.div`
  max-width: 1500px;
  width: 100%;
  position: relative;

  > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 10px;

    @media only screen and (max-width: ${({ theme }) =>
        theme.breakpoints.lg}px) {
      flex-direction: column;
      justify-content: center;
      text-align: center;

      > span {
        padding-top: 20px;
      }
    }

    h1 {
      font-weight: 700;
      color: ${({ theme }) => theme.colors.black};
      font-size: 28px;
      letter-spacing: 0px;
    }

    h3 {
      color: ${({ theme }) => theme.colors.grey};
      font-weight: 400;
      padding-top: 5px;
    }

    span {
      color: ${({ theme }) => theme.colors.softBlack};

      strong {
        font-weight: 700;
      }
    }

    button {
      display: flex;
      align-items: center;
      border: 3px solid ${({ theme }) => theme.colors.lightGrey};
      padding: 13px 15px;
      border-radius: 5px;
      background-color: ${({ theme }) => theme.colors.white};
      color: ${({ theme }) => theme.colors.softBlack};
      transition: background-color 0.2s ease;

      svg {
        font-size: 20px;
        margin-right: 8px;
        stroke-width: 2.5px;
      }

      span {
        font-family: 'Sora', sans-serif;
        font-size: 15px;
        font-weight: 600;
      }

      &:hover {
        background-color: ${({ theme }) => darken(0.03, theme.colors.white)};
      }
    }
  }
`;

export const Cards = styled.main`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(3, 128px);
  grid-gap: 30px;
  padding-top: 30px;

  div.chart-container {
    display: flex;
    flex-direction: column;
    padding: 20px;
    border: 3px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: 5px;
    grid-area: 2 / 1 / 4 / 4;

    h3 {
      font-size: 18px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.softBlack};
      padding-bottom: 20px;
    }
  }

  div.total-balance {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 20px;
    border: 3px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: 5px;
    text-align: center;
    min-width: 275px;
    grid-area: 2 / 4 / 4 / 5;

    header {
      padding: 10px 0 20px;

      h2 {
        font-size: 18px;
        font-weight: 700;
        letter-spacing: 0.25px;
        color: ${({ theme }) => theme.colors.grey};
      }
    }

    main {
      display: flex;
      flex: 1;
      color: ${({ theme }) => theme.colors.softBlack};
      justify-content: center;

      h2 {
        font-weight: 700;
        font-size: 38px;
      }

      small {
        font-weight: 800;
        font-size: 13px;
        margin: 0 3px;
      }
    }

    footer {
      width: 100%;

      a {
        display: flex;
        width: 100%;
        padding: 18px;
        background-color: ${({ theme }) => theme.colors.main};
        color: ${({ theme }) => theme.colors.white};
        text-decoration: none;
        font-weight: 600;
        font-size: 16px;
        justify-content: center;
        border-radius: 10px;
        transition: background-color 0.2s ease;

        &:hover {
          background-color: ${({ theme }) => darken(0.1, theme.colors.main)};
        }
      }
    }
  }

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoints.xxl}px) {
    grid-gap: 20px;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    grid-gap: 10px;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(6, 128px);

    div.chart-container {
      grid-area: 5 / 1 / 7 / 3;
    }

    div.total-balance {
      grid-area: 3 / 1 / 5 / 3;
    }
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: repeat(8, 128px);

    div.chart-container {
      grid-area: 7 / 1 / 9 / 2;
    }

    div.total-balance {
      grid-area: 5 / 1 / 7 / 2;
    }
  }
`;

export const CardItem = styled.div`
  flex: 1;
  height: min-content;
  display: flex;
  flex-direction: column;
  padding: 20px;
  border: 3px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 5px;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    svg {
      font-size: 24px;
    }

    span {
      font-weight: 600;

      &.negative {
        color: ${({ theme }) => theme.colors.red};
      }

      &.positive {
        color: ${({ theme }) => theme.colors.green};
      }

      &.neutral {
        color: ${({ theme }) => theme.colors.grey};
      }
    }
  }

  main {
    padding: 10px 0 5px;

    h3 {
      font-weight: 700;
      color: ${({ theme }) => theme.colors.softBlack};
      font-size: 16px;
    }
  }

  footer {
    span {
      font-size: 13px;
      color: ${({ theme }) => theme.colors.grey};
    }
  }

  &:nth-child(1) {
    grid-area: 1 / 1 / 2 / 2;
  }

  &:nth-child(2) {
    grid-area: 1 / 2 / 2 / 3;
  }

  &:nth-child(3) {
    grid-area: 1 / 3 / 2 / 4;
  }

  &:nth-child(4) {
    grid-area: 1 / 4 / 2 / 5;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    &:nth-child(1) {
      grid-area: 1 / 1 / 2 / 2;
    }

    &:nth-child(2) {
      grid-area: 1 / 2 / 2 / 3;
    }

    &:nth-child(3) {
      grid-area: 2 / 1 / 3 / 2;
    }

    &:nth-child(4) {
      grid-area: 2 / 2 / 3 / 3;
    }
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    &:nth-child(1) {
      grid-area: 1 / 1 / 2 / 2;
    }

    &:nth-child(2) {
      grid-area: 2 / 1 / 3 / 2;
    }

    &:nth-child(3) {
      grid-area: 3 / 1 / 4 / 2;
    }

    &:nth-child(4) {
      grid-area: 4 / 1 / 5 / 2;
    }
  }
`;

export const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 50px;
`;
